import { Box, Button, Chip, CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import PageHero from "../../components/motion/PageHero";
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import { useContentProtection } from "../../hooks/useContentProtection";
import { useConstants } from "../../hooks/UseConstants";

function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatAttemptDate(value, isAr) {
    if (!value) return isAr ? 'غير متوفر' : 'Not available';

    try {
        return new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(value));
    } catch {
        return value;
    }
}

function ExamTake() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { courseId, examId } = useParams();
    const draftKey = `student-exam-draft:${courseId}:${examId}`;
    const navigate = useNavigate();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [loading, setLoading] = useState(true);
    const [exam, setExam] = useState(null);
    const [started, setStarted] = useState(false);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [startedAt, setStartedAt] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const submitRef = useRef(() => {});

    useContentProtection({
        enabled: started && !result,
    });

    const clearDraft = useCallback(() => {
        try {
            localStorage.removeItem(draftKey);
        } catch {
            // Ignore storage errors so the exam flow keeps working.
        }
    }, [draftKey]);

    const loadAttempts = useCallback(async () => {
        const res = await Fetch(`${host}/student/exams/${examId}/attempts`, 'GET');
        if (res.status === 200) {
            const items = res.data?.data || [];
            setAttempts(Array.isArray(items) ? items : []);
        }
    }, [host, examId]);

    const loadExam = useCallback(async () => {
        const res = await Fetch(`${host}/courses/${courseId}/exams/${examId}/show`, 'GET');
        if (res.status === 200) setExam(res.data.data);
    }, [host, courseId, examId]);

    useEffect(() => {
        const load = async () => {
            await Promise.all([loadExam(), loadAttempts()]);
            setLoading(false);
        };
        load();
    }, [loadExam, loadAttempts]);

    useEffect(() => {
        if (!exam || started || result) return;

        const maxAttempts = exam.max_attempts ?? 3;
        const attemptsUsed = exam.attempts_used ?? 0;
        if (maxAttempts > 0 && attemptsUsed >= maxAttempts) {
            clearDraft();
            return;
        }

        try {
            const raw = localStorage.getItem(draftKey);
            if (!raw) return;

            const saved = JSON.parse(raw);
            const savedAnswers = saved?.answers && typeof saved.answers === 'object' ? saved.answers : {};
            const savedStartedAt = Number(saved?.startedAt || 0);
            const savedQuestionIndex = Number(saved?.questionIndex || 0);

            if (!savedStartedAt) {
                clearDraft();
                return;
            }

            setAnswers(savedAnswers);
            setQuestionIndex(Math.min(Math.max(savedQuestionIndex, 0), Math.max((exam.questions || []).length - 1, 0)));
            setStartedAt(savedStartedAt);
            setStarted(true);
            setSnackBar('info', isAr ? 'تمت استعادة إجاباتك السابقة لهذا الاختبار' : 'Your saved exam answers were restored');
        } catch {
            clearDraft();
        }
    }, [exam, started, result, draftKey, clearDraft, isAr, setSnackBar]);

    useEffect(() => {
        if (!started || result || !startedAt) return;

        try {
            localStorage.setItem(draftKey, JSON.stringify({
                answers,
                questionIndex,
                startedAt,
            }));
        } catch {
            // Ignore storage errors so the exam flow keeps working.
        }
    }, [started, result, startedAt, answers, questionIndex, draftKey]);

    const submit = useCallback(async (auto = false) => {
        if (submitting) return;
        const payload = Object.entries(answers).map(([question_id, answer_id]) => ({
            question_id: Number(question_id),
            answer_id: Number(answer_id),
        }));
        const totalQ = exam?.questions?.length || 0;
        if (!auto && payload.length !== totalQ) {
            setSnackBar('error', isAr ? 'أجب على جميع الأسئلة قبل الإرسال' : 'Answer all questions before submitting');
            return;
        }
        setSubmitting(true);
        const res = await Fetch(`${host}/student/exams/${examId}/submit`, 'POST', { answers: payload });
        if (res.status === 200) {
            const data = res.data.data || {};
            const elapsedMin = Math.floor((Date.now() - (startedAt || Date.now())) / 60000);
            clearDraft();
            await loadAttempts();
            setResult({
                ...data,
                percent: data.percent ?? 0,
                maxMark: data.max_mark,
                obtained: data.total_mark,
                passed: data.passed,
                correctCount: data.correct_count,
                wrongCount: data.wrong_count,
                unansweredCount: data.unanswered_count,
                elapsedMin,
                courseProgress: data.progress_percentage,
                certificateIssued: Boolean(data.certificate_issued),
            });
            setSnackBar('success', auto
                ? (isAr ? 'انتهى الوقت وتم إرسال إجاباتك تلقائياً' : 'Time is up and your answers were submitted automatically')
                : (isAr ? 'تم إرسال الإجابات بنجاح' : 'Answers submitted successfully'));
        } else {
            setSnackBar('error', res.data?.errors?.[0] || res.data?.message || (isAr ? 'فشل إرسال الإجابات' : 'Failed to submit answers'));
        }
        setSubmitting(false);
    }, [answers, exam?.questions?.length, examId, host, isAr, loadAttempts, setSnackBar, startedAt, submitting, clearDraft]);

    submitRef.current = submit;

    useEffect(() => {
        if (!started || result || !exam || !startedAt) return undefined;

        const duration = (exam.duration_minutes || 30) * 60;
        const syncRemaining = () => {
            const elapsed = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
            const remaining = Math.max(duration - elapsed, 0);
            setSecondsLeft(remaining);

            if (remaining === 0) {
                submitRef.current(true);
                return true;
            }

            return false;
        };

        if (syncRemaining()) {
            return undefined;
        }

        const interval = setInterval(() => {
            if (syncRemaining()) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [started, result, exam, startedAt]);

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress /></Box>;
    if (!exam) return <Typography>{isAr ? 'الاختبار غير موجود' : 'Exam not found'}</Typography>;

    const passAt = exam.pass_percentage ?? 50;
    const maxAttempts = exam.max_attempts ?? 3;
    const attemptsUsed = exam.attempts_used ?? 0;
    const attemptsLeft = maxAttempts > 0 ? Math.max(0, maxAttempts - attemptsUsed) : null;
    const examName = isAr ? (exam.name_ar || exam.name_en) : (exam.name_en || exam.name_ar);
    const questions = exam.questions || [];
    const totalQ = questions.length;
    const answeredCount = questions.filter((question) => answers[question.id]).length;
    const remainingCount = Math.max(totalQ - answeredCount, 0);
    const latestAttempt = attempts[0] || null;
    const bestAttempt = attempts.reduce((best, attempt) => {
        if (!best) return attempt;
        return (attempt.score_percent ?? 0) > (best.score_percent ?? 0) ? attempt : best;
    }, null);

    const attemptHistoryPanel = (
        <DashboardPanelCard
            title={isAr ? 'سجل المحاولات' : 'Attempt history'}
            subtitle={isAr
                ? `لديك ${attempts.length} محاولة مسجلة لهذا الاختبار.`
                : `You have ${attempts.length} recorded attempt(s) for this exam.`}
            className="mx-0"
        >
            {attempts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {isAr ? 'لا توجد محاولات سابقة بعد. ستظهر هنا كل نتيجة بعد إرسال الاختبار.' : 'No previous attempts yet. Each submitted result will appear here.'}
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                        {latestAttempt ? (
                            <Chip
                                label={isAr ? `آخر محاولة: ${latestAttempt.score_percent}%` : `Latest: ${latestAttempt.score_percent}%`}
                                color={latestAttempt.passed ? 'success' : 'default'}
                                variant="outlined"
                            />
                        ) : null}
                        {bestAttempt ? (
                            <Chip
                                label={isAr ? `أفضل نتيجة: ${bestAttempt.score_percent}%` : `Best score: ${bestAttempt.score_percent}%`}
                                color={bestAttempt.passed ? 'success' : 'primary'}
                                variant="outlined"
                            />
                        ) : null}
                    </Box>
                    {attempts.map((attempt, index) => {
                        const attemptNumber = attempts.length - index;
                        return (
                            <Box
                                key={attempt.id || `${attempt.submitted_at}-${index}`}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: attempt.passed ? 'rgba(34,197,94,0.28)' : 'rgba(148,163,184,0.28)',
                                    bgcolor: attempt.passed ? 'rgba(34,197,94,0.06)' : 'background.paper',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {isAr ? `المحاولة ${attemptNumber}` : `Attempt ${attemptNumber}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatAttemptDate(attempt.submitted_at, isAr)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        size="small"
                                        label={`${attempt.score_percent ?? 0}%`}
                                        color={attempt.passed ? 'success' : 'default'}
                                    />
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={attempt.passed ? (isAr ? 'ناجح' : 'Passed') : (isAr ? 'راسب' : 'Failed')}
                                        color={attempt.passed ? 'success' : 'error'}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </DashboardPanelCard>
    );

    if (!started) {
        if (attemptsLeft === 0) {
            return (
                <Box>
                    <PageHero
                        title={examName}
                        subtitle={isAr ? 'لا تتوفر محاولات إضافية لهذا الاختبار.' : 'No more attempts are available for this exam.'}
                        icon={<QuizOutlinedIcon />}
                    />
                    <DashboardPanelCard
                        title={isAr ? 'المحاولات مستنفدة' : 'Attempts exhausted'}
                        subtitle={isAr ? 'لقد استخدمت جميع المحاولات المسموح بها. يمكنك العودة إلى الدورة ومتابعة المحتوى المتبقي.' : 'You have used all allowed attempts. You can return to the course and continue the remaining content.'}
                        className="mx-0"
                    >
                        <Typography color="error.main" sx={{ fontWeight: 700, mb: 2 }}>
                            {isAr ? 'لا يمكنك بدء هذا الاختبار مرة أخرى حالياً.' : 'You cannot start this exam again right now.'}
                        </Typography>
                        <Button variant="contained" onClick={() => navigate(`/courses/${courseId}`)} sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}>
                            {isAr ? 'العودة للدورة' : 'Back to course'}
                        </Button>
                    </DashboardPanelCard>
                    <Box sx={{ mt: 3 }}>
                        {attemptHistoryPanel}
                    </Box>
                </Box>
            );
        }
        return (
            <Box>
                <PageHero
                    title={examName}
                    subtitle={isAr
                        ? `${totalQ} أسئلة · ${exam.duration_minutes || 30} دقيقة · النجاح من ${passAt}%${attemptsLeft != null ? ` · المحاولات المتبقية: ${attemptsLeft}` : ''}`
                        : `${totalQ} questions · ${exam.duration_minutes || 30} minutes · Pass at ${passAt}%${attemptsLeft != null ? ` · Attempts left: ${attemptsLeft}` : ''}`}
                    icon={<QuizOutlinedIcon />}
                    actions={
                        <Button
                            variant="contained"
                            onClick={() => {
                                clearDraft();
                                setAnswers({});
                                setStarted(true);
                                setStartedAt(Date.now());
                                setQuestionIndex(0);
                            }}
                            sx={{ bgcolor: '#fff', color: '#0D358C', fontWeight: 700 }}
                        >
                            {isAr ? 'ابدأ الاختبار الآن' : 'Start exam now'}
                        </Button>
                    }
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'عدد الأسئلة' : 'Questions'}
                            value={totalQ}
                            icon={<ChecklistOutlinedIcon />}
                            className="w-full mx-0"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'مدة الاختبار' : 'Duration'}
                            value={`${exam.duration_minutes || 30} ${isAr ? 'دقيقة' : 'min'}`}
                            icon={<AccessTimeOutlinedIcon />}
                            accent="sky"
                            className="w-full mx-0"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'نسبة النجاح' : 'Pass score'}
                            value={`${passAt}%`}
                            icon={<WorkspacePremiumOutlinedIcon />}
                            accent="teal"
                            className="w-full mx-0"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'المحاولات المتبقية' : 'Attempts left'}
                            value={attemptsLeft != null ? attemptsLeft : (isAr ? 'غير محدد' : 'Unlimited')}
                            icon={<CheckCircleOutlineIcon />}
                            accent="indigo"
                            className="w-full mx-0"
                        />
                    </Box>
                </Box>

                <DashboardPanelCard
                    title={isAr ? 'قبل أن تبدأ' : 'Before you begin'}
                    subtitle={isAr ? 'راجع التعليمات التالية لتجنب فقدان الإجابات أو انتهاء الوقت دون إرسال.' : 'Review these instructions to avoid losing answers or running out of time before submission.'}
                    className="mx-0"
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">{isAr ? 'أجب على جميع الأسئلة قبل الإرسال النهائي.' : 'Answer all questions before the final submission.'}</Typography>
                        <Typography variant="body2">{isAr ? 'يمكنك التنقل بين الأسئلة بحرية أثناء الوقت المتاح.' : 'You can navigate freely between questions during the allotted time.'}</Typography>
                        <Typography variant="body2">{isAr ? 'عند انتهاء الوقت سيتم إرسال الإجابات الحالية تلقائياً.' : 'When time runs out, your current answers are submitted automatically.'}</Typography>
                        <Typography variant="body2">{isAr ? 'بعد إرسال الاختبار ستظهر لك النتيجة مباشرة إذا كانت متاحة.' : 'After submitting, your result will be shown immediately when available.'}</Typography>
                    </Box>
                </DashboardPanelCard>
                <Box sx={{ mt: 3 }}>
                    {attemptHistoryPanel}
                </Box>
            </Box>
        );
    }

    if (result) {
        return (
            <Box>
                <PageHero
                    title={isAr ? 'نتيجة الاختبار' : 'Exam result'}
                    subtitle={`${result.percent}% — ${result.passed ? (isAr ? 'ناجح' : 'Passed') : (isAr ? 'راسب' : 'Failed')}`}
                    icon={<QuizOutlinedIcon />}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'الدرجة' : 'Score'}
                            value={`${result.obtained ?? 0} / ${result.maxMark ?? 0}`}
                            icon={<WorkspacePremiumOutlinedIcon />}
                            className="w-full mx-0"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'النسبة' : 'Percent'}
                            value={`${result.percent ?? 0}%`}
                            icon={<ChecklistOutlinedIcon />}
                            accent="sky"
                            className="w-full mx-0"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'إجابات صحيحة' : 'Correct answers'}
                            value={result.correctCount ?? '—'}
                            icon={<CheckCircleOutlineIcon />}
                            accent="teal"
                            className="w-full mx-0"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'الوقت المستغرق' : 'Elapsed time'}
                            value={`~${result.elapsedMin ?? 0} ${isAr ? 'د' : 'min'}`}
                            icon={<AccessTimeOutlinedIcon />}
                            accent="indigo"
                            className="w-full mx-0"
                        />
                    </Box>
                </Box>

                <DashboardPanelCard
                    title={isAr ? 'ملخص النتيجة' : 'Result summary'}
                    subtitle={isAr ? 'تفاصيل أدائك في هذه المحاولة.' : 'A closer look at your performance in this attempt.'}
                    className="mx-0"
                >
                    <Chip
                        label={result.passed ? (isAr ? 'ناجح' : 'Passed') : (isAr ? 'راسب' : 'Failed')}
                        color={result.passed ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">{isAr ? 'إجابات خاطئة' : 'Wrong answers'}: {result.wrongCount ?? '—'}</Typography>
                        {result.unansweredCount > 0 ? (
                            <Typography variant="body2">{isAr ? 'غير مجاب عنها' : 'Unanswered'}: {result.unansweredCount}</Typography>
                        ) : null}
                        {result.courseProgress != null ? (
                            <Typography variant="body2">{isAr ? 'تقدمك في الدورة' : 'Course progress'}: {result.courseProgress}%</Typography>
                        ) : null}
                        {result.certificateIssued ? (
                            <Typography variant="body2" color="success.main" fontWeight={700} sx={{ mt: 1 }}>
                                {isAr
                                    ? 'مبروك! اجتزت متطلبات الدورة وصدرت شهادتك. يمكنك تحميلها من صفحة الشهادات.'
                                    : 'Congratulations! You met the course requirements and your certificate has been issued. Download it from Certificates.'}
                            </Typography>
                        ) : result.passed ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {isAr
                                    ? 'للحصول على الشهادة: أكمل جميع دروس الدورة واجتز كل اختباراتها بنجاح.'
                                    : 'To earn your certificate: complete all lessons and pass every course exam.'}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
                        {result.certificateIssued ? (
                            <Button
                                variant="contained"
                                onClick={() => navigate('/certificates?tab=verify')}
                                sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
                            >
                                {isAr ? 'عرض الشهادة' : 'View certificate'}
                            </Button>
                        ) : null}
                        <Button
                            variant="contained"
                            onClick={() => navigate(`/courses/${courseId}`)}
                            sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}
                        >
                            {isAr ? 'العودة للدورة' : 'Back to course'}
                        </Button>
                    </Box>
                </DashboardPanelCard>
                <Box sx={{ mt: 3 }}>
                    {attemptHistoryPanel}
                </Box>
            </Box>
        );
    }

    const q = questions[questionIndex];

    return (
        <Box>
            <PageHero
                title={examName}
                subtitle={isAr
                    ? `السؤال ${questionIndex + 1} من ${totalQ} · الوقت المتبقي ${formatTime(secondsLeft)}`
                    : `Question ${questionIndex + 1} of ${totalQ} · ${formatTime(secondsLeft)} remaining`}
                icon={<QuizOutlinedIcon />}
                actions={
                    <Button
                        variant="contained"
                        onClick={() => submit(false)}
                        disabled={submitting}
                        sx={{ bgcolor: '#fff', color: '#0D358C', fontWeight: 700 }}
                    >
                        {submitting ? <CircularProgress size={18} color="inherit" /> : (isAr ? 'إرسال الإجابات' : 'Submit answers')}
                    </Button>
                }
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
                <Box>
                    <DashboardStatCard
                        label={isAr ? 'الوقت المتبقي' : 'Time left'}
                        value={formatTime(secondsLeft)}
                        icon={<AccessTimeOutlinedIcon />}
                        accent={secondsLeft < 60 ? 'teal' : 'sky'}
                        className="w-full mx-0"
                    />
                </Box>
                <Box>
                    <DashboardStatCard
                        label={isAr ? 'تمت الإجابة' : 'Answered'}
                        value={answeredCount}
                        icon={<CheckCircleOutlineIcon />}
                        accent="teal"
                        className="w-full mx-0"
                    />
                </Box>
                <Box>
                    <DashboardStatCard
                        label={isAr ? 'متبقي' : 'Remaining'}
                        value={remainingCount}
                        icon={<ChecklistOutlinedIcon />}
                        accent="indigo"
                        className="w-full mx-0"
                    />
                </Box>
            </Box>

            <DashboardPanelCard
                title={isAr ? 'التنقل بين الأسئلة' : 'Question navigator'}
                subtitle={isAr ? 'اضغط على رقم السؤال للانتقال إليه مباشرة.' : 'Click a question number to jump directly to it.'}
                className="mx-0 !mb-4"
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {questions.map((question, index) => {
                        const answered = Boolean(answers[question.id]);
                        const active = index === questionIndex;
                        return (
                            <Chip
                                key={question.id}
                                clickable
                                onClick={() => setQuestionIndex(index)}
                                label={index + 1}
                                color={active ? 'primary' : answered ? 'success' : 'default'}
                                variant={active ? 'filled' : 'outlined'}
                            />
                        );
                    })}
                </Box>
            </DashboardPanelCard>

            {q ? (
                <DashboardPanelCard
                    title={isAr ? `السؤال ${questionIndex + 1}` : `Question ${questionIndex + 1}`}
                    subtitle={isAr ? `درجة السؤال: ${q.full_mark ?? 1}` : `Question mark: ${q.full_mark ?? 1}`}
                    className="mx-0"
                >
                    <Typography fontWeight={700} sx={{ mb: 2, lineHeight: 1.8 }}>
                        {(isAr ? q.text_ar : q.text_en) || q.text_ar || q.text_en}
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            value={answers[q.id] || ''}
                            onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                        >
                            {(q.options || []).map((opt) => (
                                <FormControlLabel
                                    key={opt.id}
                                    value={String(opt.id)}
                                    control={<Radio />}
                                    label={(isAr ? opt.text_ar : opt.text_en) || opt.text_ar || opt.text_en}
                                    sx={{
                                        mx: 0,
                                        mb: 1,
                                        border: '1px solid',
                                        borderColor: answers[q.id] === String(opt.id) ? 'primary.main' : 'divider',
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.5,
                                        backgroundColor: answers[q.id] === String(opt.id) ? 'rgba(13,53,140,0.05)' : 'transparent',
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DashboardPanelCard>
            ) : null}

            <Box sx={{ display: 'flex', gap: 1, mt: 3, mb: 2 }}>
                <Button
                    variant="outlined"
                    disabled={questionIndex === 0}
                    onClick={() => setQuestionIndex((i) => i - 1)}
                    sx={{ flex: 1 }}
                >
                    {isAr ? 'السابق' : 'Previous'}
                </Button>
                <Button
                    variant="outlined"
                    disabled={questionIndex >= totalQ - 1}
                    onClick={() => setQuestionIndex((i) => i + 1)}
                    sx={{ flex: 1 }}
                >
                    {isAr ? 'التالي' : 'Next'}
                </Button>
            </Box>

            <Button
                variant="contained"
                fullWidth
                onClick={() => submit(false)}
                disabled={submitting}
                sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}
            >
                {submitting ? <CircularProgress size={22} color="inherit" /> : (isAr ? 'إرسال الإجابات' : 'Submit answers')}
            </Button>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default ExamTake;
